import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./welcomeScreen.css";

// Mobile/Tablet+Desktop split this screen uses — same 768px boundary and reactive
// window.innerWidth check as the rest of this app's own convention (e.g.
// NewAppointmentFlow.jsx's useIsDesktopTablet), just inverted since this one only needs to know
// "are we on Mobile".
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
};

// How long the splash sits on screen (from mount) before handing off to Login — long enough for
// its own entrance animation (~600ms) to actually finish playing and be seen, short enough to
// still read as a native-style launch splash, not a stalled screen.
const SPLASH_HOLD_MS = 1400;
// Fade-out just before navigating, so the handoff to Login isn't an abrupt cut.
const SPLASH_FADE_MS = 220;

// Mobile only — the full onboarding content below (skip, title/subtitle, FAB) doesn't fit the
// "quick native-app launch" feel this size wants, but the hero card itself (gradient + real
// logo + sparkles + floating chips + "Hitfit" eyebrow) is exactly the image this was asked to
// reuse as the splash — same markup as the Tablet/Desktop hero below, just without the
// title/subtitle/skip/FAB around it. Real logo (tablet_get_started.png — same file the hero
// below already uses), not the app-icon's "HF" monogram, which is a different, icon-only asset
// for the home-screen icon specifically. Plays a short entrance animation, then auto-advances
// to Login.
const MobileSplash = ({ onDone }) => {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const leaveTimer = setTimeout(() => setLeaving(true), SPLASH_HOLD_MS);
    const doneTimer = setTimeout(onDone, SPLASH_HOLD_MS + SPLASH_FADE_MS);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(doneTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`hf-splash-page${leaving ? " leaving" : ""}`}>
      <div className="hf-splash-content">
      <div className="hf-splash-hero-wrap">
        <div className="hf-welcome-hero hf-splash-hero">
          <div className="hf-welcome-hero-logo-wrap">
            <img
              className="hf-welcome-hero-logo"
              src="/img/hitfitlogo.jpg"
              alt="HitFit Studio"
            />
            <svg className="hf-welcome-sparkle sp1" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3v5M12 16v5M3 12h5M16 12h5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M6.3 6.3l2.6 2.6M15.1 15.1l2.6 2.6M17.7 6.3l-2.6 2.6M8.9 15.1l-2.6 2.6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
            <svg className="hf-welcome-sparkle sp2" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 3v5M12 16v5M3 12h5M16 12h5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <path
                d="M6.3 6.3l2.6 2.6M15.1 15.1l2.6 2.6M17.7 6.3l-2.6 2.6M8.9 15.1l-2.6 2.6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>

        <div className="hf-welcome-chip chip-cal">
          <svg viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
            <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="hf-welcome-chip chip-check">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div className="hf-welcome-chip chip-dumbbell">
          <svg viewBox="0 0 24 24" fill="none">
            <rect x="1.5" y="8.5" width="3" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <rect x="19.5" y="8.5" width="3" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8" />
            <rect x="4.5" y="10.3" width="2.2" height="3.4" rx=".6" fill="currentColor" />
            <rect x="17.3" y="10.3" width="2.2" height="3.4" rx=".6" fill="currentColor" />
            <path d="M6.7 12h10.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </div>
        <div className="hf-welcome-chip chip-kettle">
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M9 7a3 3 0 016 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M9 8.5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="12" cy="15" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </div>
      </div>

      <p className="hf-welcome-eyebrow">Hitfit</p>
      </div>
    </div>
  );
};

// Welcome screen — replaces the old red/black splash. Ported 1:1 from the approved design
// reference (web_user_v2/design-reference/vela-mobile-prototype.html, section#welcome,
// data-only="staff") rather than reinterpreted: same gradient hero card + real HitFit
// wordmark, same 4 floating icon chips, same eyebrow/title/subtitle copy, same bottom-right
// FAB. Icon SVGs below are copied verbatim from that file's <symbol> defs (i-calendar/i-check/
// i-dumbbell/i-kettlebell) — this app has no shared inline-icon module the way web_user_v2 does
// (its Icon.tsx), so they're inlined here instead of approximated with a different MUI icon.
// Auth/session logic untouched: App.js already only mounts this for `!authenticated`, and
// navigating to /login below feeds straight into the existing, unmodified login flow.
const GetStarted = () => {
  const navigate = useNavigate();
  const goToLogin = () => navigate("/login");
  const isMobile = useIsMobile();

  if (isMobile) {
    // `replace`, unlike goToLogin below — this hand-off is automatic, not a click, so the
    // splash shouldn't become a "back" destination once it's already timed out.
    return <MobileSplash onDone={() => navigate("/login", { replace: true })} />;
  }

  return (
    <div className="hf-welcome-page">
      <span className="hf-welcome-blob b1" />
      <span className="hf-welcome-blob b2" />
      <span className="hf-welcome-blob b3" />

      <div className="hf-welcome-topbar">
        <button type="button" className="hf-welcome-skip" onClick={goToLogin}>
          Preskoči
        </button>
      </div>

      <div className="hf-welcome-body">
        <div className="hf-welcome-hero-wrap">
          <div className="hf-welcome-hero">
            <div className="hf-welcome-hero-logo-wrap">
              <img
                className="hf-welcome-hero-logo"
                src="/tablet_get_started.png"
                alt="HitFit Studio"
              />
              <svg className="hf-welcome-sparkle sp1" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3v5M12 16v5M3 12h5M16 12h5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M6.3 6.3l2.6 2.6M15.1 15.1l2.6 2.6M17.7 6.3l-2.6 2.6M8.9 15.1l-2.6 2.6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <svg className="hf-welcome-sparkle sp2" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3v5M12 16v5M3 12h5M16 12h5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <path
                  d="M6.3 6.3l2.6 2.6M15.1 15.1l2.6 2.6M17.7 6.3l-2.6 2.6M8.9 15.1l-2.6 2.6"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>

          <div className="hf-welcome-chip chip-cal">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="3" y="5" width="18" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="hf-welcome-chip chip-check">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="hf-welcome-chip chip-dumbbell">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="1.5" y="8.5" width="3" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <rect x="19.5" y="8.5" width="3" height="7" rx="1" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <rect x="4.5" y="10.3" width="2.2" height="3.4" rx=".6" fill="currentColor" />
              <rect x="17.3" y="10.3" width="2.2" height="3.4" rx=".6" fill="currentColor" />
              <path d="M6.7 12h10.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </svg>
          </div>
          <div className="hf-welcome-chip chip-kettle">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M9 7a3 3 0 016 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M9 8.5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              <circle cx="12" cy="15" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </div>
        </div>

        <p className="hf-welcome-eyebrow">Hitfit</p>
        <h1 className="hf-welcome-title">
          Vaše zdravlje,
          <br />
          savršeno zakazano
        </h1>
        <p className="hf-welcome-sub">
          Zakazujte termine, upravljajte klijentima i pratite svaki sastanak — sve na jednom
          mirnom, jednostavnom mestu.
        </p>
      </div>

      <button type="button" className="hf-welcome-fab" onClick={goToLogin} aria-label="Nastavi">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
};

export default GetStarted;
